import clsx from 'clsx';
import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';

import {ROUTES, URLS} from 'constants/routes';
import {SFC} from 'types/generic';

import LOGO_FOOTER from 'assets/v3/logo-footer.png';
import gwabImg from 'assets/v3/gwab.png';

import WECHAT_QR_CODE from './contactQrCode';

/**
 * 新版页脚（v3）：6 列深色布局（Brand 2 列 + 4 个栏目），基于设计稿 Footer 区块调整而来。
 *
 * 相对设计稿的调整（业务方确认）：
 * - 社交图标（X/GitHub/LinkedIn 占位）→ 微信/电话/邮箱联系图标，点击弹窗显示联系方式。
 * - 「解决方案」列 →「帮助文档」列。
 * - 友情链接整合为版权行上方的文字列表（原独立 logo 墙区块）。
 * - ICP 备案 + 公安备案信息（原 Layout 的 renderCopyright 移植于此）。
 *
 * 对外契约：data-testid="Footer" 与 className 透传（Footer.test.tsx 依赖）。
 */

const CONTACT_PHONE = '17664075341';
const CONTACT_EMAIL = 'bussiness@tosoiot.com';

type ContactChannel = 'wechat' | 'phone' | 'email';

interface FooterLinkItem {
  title: string;
  to?: string;
  href?: string;
  onClick?: () => void;
  /** 稳定的交互标志位（i18n 后不能按 title 文案匹配）：'wechatSupport' = onClick 由组件注入弹二维码 */
  action?: 'wechatSupport';
}

interface FooterColumn {
  heading: string;
  items: FooterLinkItem[];
}

/**
 * 页脚栏目数据工厂（i18n）：文案走 common 词典。必须在组件函数体内调用。
 * friendLinks 品牌名为专有名词，保持静态模块级（见下方）。
 */
const useFooterColumns = (): FooterColumn[] => {
  const {t} = useTranslation('common');

  const productColumn: FooterColumn = {
    heading: t('footer.columns.products'),
    items: [
      {title: t('footer.items.imageTranslator'), to: ROUTES.imageTranslator},
      {title: t('footer.items.videoTranslator'), to: ROUTES.videoTranslator},
      // 五项均对齐导航栏，跳站内落地页（2026-09-16 前主图详情图/模特场景图为 workspace 外链）
      {title: t('footer.items.detailPage'), to: ROUTES.listingImages},
      {title: t('footer.items.fusion'), to: ROUTES.fusion},
      {title: t('footer.items.marketing'), to: ROUTES.marketing},
    ],
  };

  const helpColumn: FooterColumn = {
    heading: t('footer.columns.help'),
    items: [
      {title: t('footer.items.supportLanguages'), to: ROUTES.language},
      {title: t('footer.items.guide'), to: ROUTES.guide},
      {action: 'wechatSupport', title: t('footer.items.wechatSupport')}, // onClick 由组件注入（弹二维码）
      {href: URLS.developerAPIDoc, title: t('footer.items.apiDocs')}, // Apifox 新文档（业务确认保留外链）
    ],
  };

  const companyColumn: FooterColumn = {
    heading: t('footer.columns.about'),
    items: [
      {title: t('footer.items.companyIntro'), to: ROUTES.aboutUsEn},
      {title: t('footer.items.contactUs'), to: ROUTES.contact},
      {title: t('footer.items.joinUs')}, // 设计稿为 href="#" 占位
      // 协议类条目已移至「服务条款」栏；隐私政策/服务条款同时在底栏展示
    ],
  };

  /** 协议与政策类条目集中于此（业务方 2026-08 确认），原分散在「关于我们」栏 */
  const serviceTermsColumn: FooterColumn = {
    heading: t('footer.columns.serviceTerms'),
    items: [
      {title: t('footer.items.delivery'), to: ROUTES.deliveryPolicy},
      {title: t('footer.items.termsColumn'), to: ROUTES.termsOfUse},
      {title: t('footer.items.refund'), to: ROUTES.returnPolicy},
      {title: t('footer.items.privacyColumn'), to: ROUTES.privacyPolicy},
      {title: t('footer.items.subscription'), to: ROUTES.subscriptionAgreement},
    ],
  };

  return [productColumn, helpColumn, companyColumn, serviceTermsColumn];
};

/**
 * 友情链接：原独立的 FriendshipLinks logo 墙区块整合而来（用户反馈 logo 墙太占版面）。
 * 名称与 URL 以业务方提供的清单为准（2026-08），渲染为文字链接列表置于版权行上方。
 */
const friendLinks: {name: string; nofollow?: boolean; url: string}[] = [
  {name: '妙手ERP', url: 'https://erp.91miaoshou.com/'},
  {name: '客优云', url: 'https://www.keyouyun.com/'},
  {name: '芒果店长', url: 'https://www.mangoerp.com/index'},
  {name: 'Dropshipping', url: 'https://www.cjdropshipping.com/'},
  {name: '快递助手', url: 'https://erpkj.kuaidizs.cn/index.html#/loginKj?source=kj_xiangji'},
  {name: 'ShipBoss', url: 'https://www.shipboss.com/index.html#/login?source=xiangji'},
  {name: '逸淘跨境ERP', url: 'https://erp.1tsoft.com?channel=XJFY'}, // 名称取 test 分支 2026-09-05 确认的完整名
  {name: '旺销王', url: 'https://www.wxwerp.com/'},
  {name: '甩手店长', url: 'https://dz.shuaishou.com/home/index?link=xiangji'},
  {name: 'AMZ123', url: 'https://www.amz123.com/'},
  {name: 'HelpLook', url: 'https://share.helplook.net/jIBQJz'},
  {name: 'EasyBoss', url: 'https://www.easyboss.com/'},
  {name: 'Shopify', url: 'https://www.shopify.com/'},
  {name: 'Ozon', url: 'https://www.ozon.ru/'},
  {name: 'Cafe24', url: 'https://www.cafe24.com/'},
  {name: 'LX Hausys', url: 'https://www.lxhausys.com/'},
  {name: 'Meta Commerce', url: 'https://www.meta-commerce.co.kr/'},
  {name: 'Alibaba', url: 'https://www.alibaba.com/'},
  {name: 'Couplus', url: 'https://www.couplus.co.kr/'},
  {name: '店小秘', url: 'https://www.dianxiaomi.com/'},
  {name: 'BigSeller', url: 'https://www.bigseller.com/zh_CN/index.htm?sourceChannelId=1307'},
  {name: '多客', url: 'https://www.duoke.com/register.html?code=XJ'},
  {name: '店小宝', url: 'https://erp.dianxiaobao.net/'},
  {name: '马帮ERP', url: 'https://www.mabangerp.com/index.htm'},
  {name: '米境通', url: 'https://www.miwaimao.com/'},
  {name: '友蒙', url: 'https://erp.unicomlife.com/ymcomlife/#/login'},
  {name: '无忧易售', url: 'https://www.51selling.com/'},
  {name: 'UEESHOP', nofollow: true, url: 'https://www.ueeshop.com/'}, // 原组件即带 nofollow
];

const renderLink = (item: FooterLinkItem) => {
  const className = 'hover:text-white transition-colors';
  if (item.onClick) {
    return (
      <li key={item.title}>
        <button className={className} onClick={item.onClick} type="button">
          {item.title}
        </button>
      </li>
    );
  }
  if (item.to) {
    return (
      <li key={item.title}>
        <Link className={className} to={item.to}>
          {item.title}
        </Link>
      </li>
    );
  }
  if (item.href) {
    return (
      <li key={item.title}>
        <a className={className} href={item.href} rel="noreferrer" target="_blank">
          {item.title}
        </a>
      </li>
    );
  }
  return (
    <li className="text-slate-400" key={item.title}>
      {item.title}
    </li>
  );
};

/** 联系方式图标（微信/电话/邮箱，heroicons outline 风格），点击打开联系方式弹窗 */
const contactIconPaths: Record<Exclude<ContactChannel, never>, string> = {
  email:
    'M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75',
  phone:
    'M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z',
  wechat:
    'M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z',
};

const Footer: SFC = ({className}) => {
  const [contactModal, setContactModal] = useState<ContactChannel | null>(null);
  const closeContactModal = () => setContactModal(null);
  const {t} = useTranslation('common');
  const footerColumns = useFooterColumns();

  const contactIconLabels: Record<Exclude<ContactChannel, never>, string> = {
    email: t('footer.icons.email'),
    phone: t('footer.icons.phone'),
    wechat: t('footer.icons.wechat'),
  };

  /** 「帮助文档」列的「微信客服」在渲染时注入弹窗回调。按稳定 action 标志位匹配，与文案语言无关 */
  const withContactHandlers = (items: FooterLinkItem[]) =>
    items.map((item) => (item.action === 'wechatSupport' ? {...item, onClick: () => setContactModal('wechat')} : item));

  /** ICP/公安备案。按部署域名区分主体（逻辑照抄旧 Layout 的 renderCopyright） */
  const renderIcp = () => {
    if (typeof window !== 'undefined' && window.location.host === 'www.tosoiot.com') {
      return (
        <>
          <li>{t('footer.copyrightShandong')}</li>
          <li>
            <a className="hover:text-slate-300" href="http://beian.miit.gov.cn" rel="noreferrer" target="_blank">
              鲁ICP备2021031214号-1
            </a>
          </li>
          <li className="flex items-center justify-center gap-1">
            <img alt="gongan" className="inline-block" src={gwabImg} style={{height: 14, verticalAlign: 'top'}} />
            {t('footer.gongan')}
          </li>
        </>
      );
    }
    return (
      <>
        <li>{t('footer.copyrightSuzhou')}</li>
        <li>
          <a className="hover:text-slate-300" href="http://beian.miit.gov.cn" rel="noreferrer" target="_blank">
            苏ICP备2023007092号-4
          </a>
        </li>
      </>
    );
  };

  /** 联系方式弹窗内容 */
  const renderContactModalBody = () => {
    if (contactModal === 'wechat') {
      return (
        <>
          <h4 className="text-base font-semibold text-slate-900 mb-4">{t('footer.modal.wechatTitle')}</h4>
          <img alt="微信客服二维码" className="w-44 h-44 mx-auto rounded-md" decoding="async" src={WECHAT_QR_CODE} />
          <p className="text-xs text-slate-500 mt-3">{t('footer.modal.scanTip')}</p>
        </>
      );
    }
    if (contactModal === 'phone') {
      return (
        <>
          <h4 className="text-base font-semibold text-slate-900 mb-4">{t('footer.modal.phoneTitle')}</h4>
          <a
            className="flex items-center justify-center gap-2 text-xl font-semibold text-slate-900"
            href={`tel:${CONTACT_PHONE}`}
          >
            <svg
              className="w-5 h-5 text-indigo-600"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.5}
              viewBox="0 0 24 24"
            >
              <path d={contactIconPaths.phone} strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {CONTACT_PHONE}
          </a>
        </>
      );
    }
    return (
      <>
        <h4 className="text-base font-semibold text-slate-900 mb-4">{t('footer.modal.emailTitle')}</h4>
        <a
          className="flex items-center justify-center gap-2 text-base text-indigo-600 break-all"
          href={`mailto:${CONTACT_EMAIL}`}
        >
          <svg
            className="w-5 h-5 shrink-0 text-indigo-600"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            viewBox="0 0 24 24"
          >
            <path d={contactIconPaths.email} strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          {CONTACT_EMAIL}
        </a>
      </>
    );
  };

  return (
    // v3-scope 必须是纯容器：工具类编译为 `.v3-scope .xxx` 后代选择器，
    // 与 v3-scope 同元素时永不匹配（深色背景等会静默失效）。见 CLAUDE.md「v3 样式体系」。
    <div className="v3-scope">
      <footer className={clsx('bg-slate-900 text-slate-300 py-16', className)} data-testid="Footer">
        <div className="max-w-[90rem] mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-12 mb-12">
            {/* Brand */}
            <div className="lg:col-span-2">
              <img
                alt="象寄翻译 Xiangji AI"
                className="h-9 w-auto mb-6"
                decoding="async"
                height={210}
                src={LOGO_FOOTER}
                width={575}
              />
              <p className="text-sm text-slate-400 leading-relaxed mb-6 max-w-sm">{t('footer.brandDesc')}</p>
              {/* 联系方式图标：点击弹窗 */}
              <div className="flex gap-4">
                {(['wechat', 'phone', 'email'] as ContactChannel[]).map((channel) => (
                  <button
                    aria-label={contactIconLabels[channel]}
                    className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center hover:bg-slate-700 transition-colors"
                    key={channel}
                    onClick={() => setContactModal(channel)}
                    type="button"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                      <path d={contactIconPaths[channel]} strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                ))}
              </div>
            </div>

            {footerColumns.map((column) => (
              <div key={column.heading}>
                {/* 栏目名用 p 而非 h4：页脚与正文 heading 层级不可控（多数页面止于 h2），h4 会触发 heading-order 跳级 */}
                <p className="text-white font-semibold mb-4">{column.heading}</p>
                <ul className="space-y-3 text-sm">{withContactHandlers(column.items).map(renderLink)}</ul>
              </div>
            ))}
          </div>

          {/* 友情链接：文字链接列表（原独立 logo 墙区块整合于此），换行自然发生在「友情链接：」标签之后 */}
          <div className="border-t border-slate-800 pt-4 mb-4 flex flex-wrap items-center gap-x-8 gap-y-2">
            <span className="text-xs text-slate-400">{t('footer.friendsLabel')}</span>
            {friendLinks.map((link) => (
              <a
                className="text-xs text-slate-400 hover:text-white transition-colors"
                href={link.url}
                key={link.url}
                rel={link.nofollow ? 'noreferrer nofollow' : 'noreferrer'}
                target="_blank"
              >
                {link.name}
              </a>
            ))}
          </div>

          <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            {/* slate-400：深色底（bg-slate-900）上 slate-500 对比度仅 3.75，低于 WCAG AA 的 4.5 */}
            <div className="flex flex-col md:flex-row items-center gap-4 md:gap-8 text-sm text-slate-400">
              <p>&copy; 2025 www.xiangjifanyi.com, All Rights Reserved.</p>
              <ul className="flex flex-col md:flex-row items-center gap-4 md:gap-8 text-center">{renderIcp()}</ul>
            </div>
            <div className="flex items-center gap-6 text-sm text-slate-400">
              <Link className="hover:text-white transition-colors" to={ROUTES.privacyPolicy}>
                {t('footer.items.privacyBottom')}
              </Link>
              <Link className="hover:text-white transition-colors" to={ROUTES.termsOfUse}>
                {t('footer.items.termsBottom')}
              </Link>
              <span>{t('footer.items.cookie')}</span> {/* 设计稿为 href="#" 占位 */}
            </div>
          </div>
        </div>
      </footer>
      {/* 联系方式弹窗：渲染在 v3-scope 内以使用 Tailwind 类（勿移出，工具类将失效） */}
      {contactModal && (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- 遮罩点击关闭为标准弹窗交互，键盘用户走右上角关闭按钮
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60"
          onClick={closeContactModal}
        >
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- 阻止冒泡到遮罩 */}
          <div
            className="relative bg-white rounded-xl p-6 w-full max-w-xs text-center"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 右上角关闭按钮（原底部文字「关闭」已移除，词典 close 键转用作其 aria-label） */}
            <button
              aria-label={t('footer.modal.close')}
              className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
              onClick={closeContactModal}
              type="button"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {renderContactModalBody()}
          </div>
        </div>
      )}
    </div>
  );
};

export default Footer;
